import React from 'react'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {DeadlineStickerValue, Hub} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {
  ColConfig,
  ColumnVisiblity,
  SortType
} from '@components/common/sandbox/list-view/types'
import {
  TaskAuthorColumn,
  TaskDeadlineColumn,
  TaskFavoritesColumn,
  TaskStickersColumn,
  TaskUsersColumn
} from '@components/my-tasks/tasks-list/columns'
import {TaskLocationColumn} from '@components/my-tasks/tasks-list/columns/task-location-column'
import {Sticker, systemStickerCtl} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'

export const getColumns = (): ColConfig<Hub>[] => {
  return [
    {
      id: 'name',
      shortName: '',
      fullName: '',
      sort: {
        icon: 'IconSortAZ',
        title: Lang.msg.myTasks.orderBy.taskName,
        sortBy: data => data.title,
        type: SortType.Alphabetical
      },
      /**
       * This column is always hidden, because list-view doesn't use columns to render the item name.
       * But it still needs to be here to be able to sort by task name.
       */
      visibility: ColumnVisiblity.AlwaysHidden,
      isDefault: true
    },
    {
      id: 'favorites',
      shortName: '', // We do not show the title for favorites column
      fullName: Lang.msg.taskPage.starredTab,
      width: 45,
      sort: {
        icon: 'star',
        title: Lang.msg.columnOrder.orderByFavorites,
        sortBy: data => {
          return UserSettings.isStarred(data) ? 0 : 1
        },
        type: SortType.Favorites
      },
      hideOnDrag: true,
      renderer: () => <TaskFavoritesColumn />
    },
    {
      id: 'assigners',
      shortName: `${Lang.msg.chat.assigned.slice(0, 6)}.`,
      fullName: Lang.msg.chat.assigned,
      width: 52,
      sort: {
        icon: 'user',
        title: Lang.msg.columnOrder.orderByAssigners,
        sortBy: data => {
          return Sticker.getUsers(data)
            .slice(1)
            .map(userId => Accounts.get(userId).realName)
            .join(', ')
        },
        type: SortType.Alphabetical
      },
      hideOnDrag: true,
      renderer: () => <TaskUsersColumn />,
      isDefault: true
    },
    {
      id: 'author',
      shortName: Lang.msg.fieldCreatedBy,
      fullName: Lang.msg.fieldCreatedBy,
      width: 52,
      sort: {
        icon: 'user',
        title: Lang.msg.columnOrder.orderByAuthor,
        sortBy: data => {
          const authorId = Task.creator(data)?.id
          return authorId ? Accounts.get(authorId).realName : ''
        },
        type: SortType.Alphabetical
      },
      hideOnDrag: true,
      renderer: () => <TaskAuthorColumn />
    },
    {
      id: 'location',
      shortName: Lang.msg.myTasks.columns.location,
      fullName: Lang.msg.myTasks.columns.location,
      width: 114,
      hideOnDrag: true,
      renderer: () => <TaskLocationColumn />,
      isDefault: true
    },
    {
      id: 'createdAt',
      fullName: Lang.msg.statsFilterCreatedAt,
      shortName: Lang.msg.statFilterDate,
      width: 150,
      sort: {
        icon: 'IconCalendar',
        title: Lang.msg.columnOrder.orderByDateCreation,
        sortBy: data => data.timestamp,
        type: SortType.Date
      },
      renderer: row => (
        <div className="text-xs-regular">
          {Time.formatDateTime(row.data.timestamp)}
        </div>
      ),
      hideOnDrag: true
    },
    {
      id: 'deadline',
      fullName: Lang.msg.deadline,
      shortName: Lang.msg.deadline,
      sort: {
        icon: 'IconCustomCalendar',
        title: Lang.msg.columnOrder.orderByDeadline,
        sortBy: (data, asc) => {
          const box = Sync.getCurrentSandbox()
          const ctl = systemStickerCtl.Deadline
          const sticker = ctl.getSticker(box)
          const stickerValue: DeadlineStickerValue = data.stickers[sticker.id]

          return (
            stickerValue?.current?.deadline ||
            (asc ? Number.MAX_VALUE : Number.MIN_VALUE)
          )
        },
        type: SortType.Date
      },
      width: 200,
      hideOnDrag: true,
      isDefault: true,
      renderer: row => <TaskDeadlineColumn taskId={row.data.id} />
    },
    {
      id: 'stickers',
      fullName: Lang.msg.myTasks.columns.stickers,
      shortName: Lang.msg.myTasks.columns.stickers,
      hideOnDrag: true,
      width: '2fr',
      renderer: () => <TaskStickersColumn />,
      isDefault: true
    }
  ]
}
